// 💾 src/pages/admin/components/AdminAddTradeForm.tsx
import { useEffect, useState } from "react"
import { db } from "../../../services/firebase"
import {
  doc,
  collection,
  addDoc,
  serverTimestamp,
  getDoc,
  updateDoc,
  getDocs
} from "firebase/firestore"

type User = {
  id: string
  fullName: string
  email: string
}

const AdminAddTradeForm = () => {
  const [users, setUsers] = useState<User[]>([])
  const [userId, setUserId] = useState("")
  const [crypto, setCrypto] = useState("")
  const [tradeAction, setTradeAction] = useState("BUY")
  const [tradeAmount, setTradeAmount] = useState("")
  const [tradePrice, setTradePrice] = useState("")
  const [priceNow, setPriceNow] = useState("")
  const [profitLose, setProfitLose] = useState("")
  const [message, setMessage] = useState("")

  // 🔄 Fetch users for dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, "users"))
      const list: User[] = snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          fullName: data.fullName || data.displayName || "Unknown",
          email: data.email || "Unknown"
        }
      })
      setUsers(list)
    }
    fetchUsers()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage("")

    try {
      const userRef = doc(db, "users", userId)
      const tradesRef = collection(userRef, "trades")

      // 1️⃣ Create trade
      const tradeData = {
        createdAt: serverTimestamp(),
        crypto,
        orderType: "market",
        priceNow: Number(priceNow),
        profitLose: Number(profitLose),
        side: tradeAction.toLowerCase(), // "buy" | "sell"
        sl: false,
        status: "CLOSED",
        tp: false,
        tradeAction,
        tradeAmount: Number(tradeAmount),
        tradePrice: Number(tradePrice),
        tradedBy: "AI"
      }
      const tradeRef = await addDoc(tradesRef, tradeData)

      // 2️⃣ Add notification
      const notificationsRef = collection(userRef, "notifications")
      await addDoc(notificationsRef, {
        message: `✅ Trade Closed: ${crypto} — Profit $${profitLose}`,
        seen: "No",
        timestamp: serverTimestamp()
      })

      // 3️⃣ Update balance (assuming /users/{uid}/balance/main)
      const balanceRef = doc(userRef, "balance", "main")
      const balanceSnap = await getDoc(balanceRef)
      if (balanceSnap.exists()) {
        const currentAmount = balanceSnap.data().amount || 0
        await updateDoc(balanceRef, {
          amount: currentAmount + Number(profitLose)
        })
      }

      setMessage(`✅ Trade added (ID: ${tradeRef.id})`)

      // Reset form
      setUserId("")
      setCrypto("")
      setTradeAction("BUY")
      setTradeAmount("")
      setTradePrice("")
      setPriceNow("")
      setProfitLose("")
    } catch (err: any) {
      console.error("❌ Failed to add trade:", err)
      setMessage(`❌ ${err.message}`)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="admin-add-trade">
      <h3>Add Trade</h3>

      <div>
        <label>User</label>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          required
        >
          <option value="">-- Select User --</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.id} — {u.fullName}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label>Crypto</label>
        <input
          value={crypto}
          onChange={(e) => setCrypto(e.target.value)}
          placeholder="BTC/USDT"
          required
        />
      </div>

      <div>
        <label>Action</label>
        <select value={tradeAction} onChange={(e) => setTradeAction(e.target.value)}>
          <option value="BUY">Buy</option>
          <option value="SELL">Sell</option>
        </select>
      </div>

      <div>
        <label>Amount</label>
        <input
          type="number"
          value={tradeAmount}
          onChange={(e) => setTradeAmount(e.target.value)}
          required
        />
      </div>

      <div>
        <label>Trade Price</label>
        <input
          type="number"
          value={tradePrice}
          onChange={(e) => setTradePrice(e.target.value)}
          required
        />
      </div>

      <div>
        <label>Price Now</label>
        <input
          type="number"
          value={priceNow}
          onChange={(e) => setPriceNow(e.target.value)}
          required
        />
      </div>

      <div>
        <label>Profit/Loss</label>
        <input
          type="number"
          value={profitLose}
          onChange={(e) => setProfitLose(e.target.value)}
          required
        />
      </div>

      <button type="submit">Add Trade</button>
      {message && <p>{message}</p>}
    </form>
  )
}

export default AdminAddTradeForm
